import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminEvents, adminDeleteEvent } from '../../hooks/useAdmin';
import { formatDate, formatTime } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

export default function AdminEventsPage() {
  const { events, loading, pagination, fetchEvents } = useAdminEvents();
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    fetchEvents(search, 0);
  }

  function goToPage(page: number) {
    fetchEvents(search, page);
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleToggleVisibility(eventId: string, currentVisibility: string, title: string) {
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
    const { error } = await supabase.from('events').update({ visibility: newVisibility }).eq('id', eventId);
    if (error) {
      showMessage('error', error.message);
    } else {
      showMessage('success', `"${title}" is now ${newVisibility}`);
      fetchEvents(search, pagination.page);
    }
  }

  async function handleDelete(eventId: string, title: string) {
    if (!confirm(`Delete event "${title}"? This cannot be undone.`)) return;

    const { error } = await adminDeleteEvent(eventId);
    if (error) {
      showMessage('error', error);
    } else {
      showMessage('success', `Deleted "${title}"`);
      fetchEvents(search, pagination.page);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 hover:text-gray-50 transition">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold">Manage Events</h1>
      </div>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-200' : 'bg-red-900/50 border border-red-700 text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by event title, venue, or band name..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            Search
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-400">
              {pagination.totalCount} event{pagination.totalCount !== 1 ? 's' : ''}
              {pagination.totalPages > 1 && ` — page ${pagination.page + 1} of ${pagination.totalPages}`}
            </p>
          </div>
          {events.map((event) => (
            <div key={event.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{event.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      event.event_type === 'gig'
                        ? 'bg-indigo-900/50 text-indigo-300'
                        : event.event_type === 'rehearsal'
                          ? 'bg-emerald-900/50 text-emerald-300'
                          : 'bg-gray-800 text-gray-400'
                    }`}>
                      {event.event_type}
                    </span>
                    {event.visibility === 'public' ? (
                      <span className="bg-green-900/50 text-green-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">public</span>
                    ) : (
                      <span className="bg-yellow-900/50 text-yellow-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">private</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">
                    {formatDate(event.start_time)} at {formatTime(event.start_time)}
                    {event.venue_name && ` | ${event.venue_name}`}
                  </p>
                  {event.band && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Band:{' '}
                      <Link to={`/band/${event.band.slug}`} className="text-indigo-400 hover:text-indigo-300">
                        {event.band.name}
                      </Link>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleToggleVisibility(event.id, event.visibility, event.title)}
                    className="text-gray-400 hover:text-amber-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                    title={event.visibility === 'public' ? 'Make private' : 'Make public'}
                  >
                    {event.visibility === 'public' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(event.id, event.title)}
                    className="text-gray-400 hover:text-red-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                    title="Delete event"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i)
                .filter((i) => i === 0 || i === pagination.totalPages - 1 || Math.abs(i - pagination.page) <= 1)
                .reduce<(number | 'ellipsis')[]>((acc, i, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== i - 1) acc.push('ellipsis');
                  acc.push(i);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`e${idx}`} className="text-gray-500 px-1">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => goToPage(item)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                        item === pagination.page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-50'
                      }`}
                    >
                      {item + 1}
                    </button>
                  )
                )}
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
